'use client';

import React, { useState } from 'react';
import {
    CreditCard,
    Building2,
    Smartphone,
    ShieldCheck,
    Info,
    ChevronDown,
    ChevronUp,
    Copy,
    CheckCircle2,
    QrCode,
    HelpCircle,
    ArrowRight
} from 'lucide-react';

interface PaymentMethod {
    id: string;
    type: 'Automated' | 'Manual';
    name: string;
    provider: string;
    accountNo?: string;
    accountName?: string;
    description: string;
    instructions: string[];
}

const paymentMethods: PaymentMethod[] = [
    {
        id: '1',
        type: 'Automated',
        name: 'Virtual Account (VA)',
        provider: 'Bank Syariah Indonesia (BSI)',
        description: 'Verifikasi instan, tidak perlu unggah bukti transfer.',
        instructions: [
            'Pilih metode Virtual Account saat checkout tagihan.',
            'Salin nomor VA yang muncul.',
            'Buka aplikasi mobile banking atau ATM.',
            'Pilih menu Pembayaran > Institusi / Akademik.',
            'Masukkan nomor VA dan konfirmasi nominal.',
            'Status tagihan akan otomatis berubah menjadi Lunas dalam 5 menit.'
        ]
    },
    {
        id: '2',
        type: 'Manual',
        name: 'Transfer Bank Manual',
        provider: 'Bank Mandiri',
        accountNo: '102-00-1122334-5',
        accountName: 'YAYASAN PENDIDIKAN AL-FATIH',
        description: 'Membutuhkan verifikasi admin maksimal 1x24 jam.',
        instructions: [
            'Lakukan transfer ke nomor rekening yang tertera.',
            'Pastikan nominal transfer sesuai hingga 3 digit terakhir.',
            'Simpan bukti transfer dalam format foto (JPG / PNG).',
            'Buka menu Tagihan > Pilih Bayar > Upload Bukti.',
            'Tunggu verifikasi admin sekolah.'
        ]
    },
    {
        id: '3',
        type: 'Automated',
        name: 'QRIS / E-Wallet',
        provider: 'LinkAja, Dana, ShopeePay',
        description: 'Verifikasi otomatis melalui scan barcode.',
        instructions: [
            'Pilih metode QRIS saat checkout.',
            'Tampilkan QR Code di layar.',
            'Scan menggunakan aplikasi pembayaran pilihan Anda.',
            'Konfirmasi pembayaran dan masukkan PIN.',
            'Jangan tutup halaman sebelum konfirmasi berhasil muncul.'
        ]
    }
];

export default function PaymentMethods() {
    const [expandedMethod, setExpandedMethod] = useState<string | null>('1');

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        // You would typically show a toast here
    };

    return (
        <div className="space-y-10 animate-in fade-in duration-700 pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 min-h-[80px]">
                <div>
                    <h1 className="text-3xl font-black text-foreground tracking-tight flex items-center gap-4">
                        <CreditCard className="w-8 h-8 text-primary" /> Metode Pembayaran
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 font-medium italic">Informasi resmi saluran pembayaran sekolah yang sah dan aman.</p>
                </div>
                <div className="bg-primary-soft/50 border border-primary/20 p-4 rounded-2xl flex items-center gap-3">
                    <ShieldCheck className="w-6 h-6 text-primary" />
                    <div>
                        <p className="text-[10px] font-black text-primary uppercase leading-none mb-1">Terverifikasi</p>
                        <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest leading-none">Keamanan Data Bank Sekolah</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left: Accounts & Methods */}
                <div className="lg:col-span-12 xl:col-span-8 space-y-8">
                    {/* Official Bank Accounts */}
                    <div className="bg-surface border border-border-main rounded-[40px] p-10 shadow-xs space-y-8">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-black flex items-center gap-3">
                                <Building2 className="w-6 h-6 text-primary" /> Rekening Resmi Yayasan
                            </h2>
                            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] italic">Read-Only Access</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="p-8 bg-background border border-border-main rounded-[32px] space-y-4 group hover:border-primary/50 transition-all">
                                <div className="flex items-center justify-between">
                                    <div className="w-12 h-12 bg-primary/5 rounded-2xl flex items-center justify-center text-primary font-black italic">BSI</div>
                                    <button
                                        onClick={() => copyToClipboard('7122010211')}
                                        className="p-2.5 hover:bg-primary-soft rounded-xl transition-colors text-muted-foreground hover:text-primary"
                                    >
                                        <Copy className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Bank Syariah Indonesia (BSI)</p>
                                    <p className="text-xl font-black text-foreground tracking-widest">7122 0102 11</p>
                                    <p className="text-xs font-bold text-muted-foreground">a.n YAYASAN AL-FATIH PEKANBARU</p>
                                </div>
                            </div>

                            <div className="p-8 bg-background border border-border-main rounded-[32px] space-y-4 group hover:border-primary/50 transition-all">
                                <div className="flex items-center justify-between">
                                    <div className="w-12 h-12 bg-primary/5 rounded-2xl flex items-center justify-center text-primary font-black italic">BM</div>
                                    <button
                                        onClick={() => copyToClipboard('1020011223345')}
                                        className="p-2.5 hover:bg-primary-soft rounded-xl transition-colors text-muted-foreground hover:text-primary"
                                    >
                                        <Copy className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Bank Mandiri</p>
                                    <p className="text-xl font-black text-foreground tracking-widest">102-00-1122334-5</p>
                                    <p className="text-xs font-bold text-muted-foreground">a.n YPI AL-FATIH PUSAT</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* QRIS Placeholder */}
                    <div className="bg-primary/5 border border-primary/20 rounded-[40px] p-10 flex flex-col md:flex-row items-center gap-10 overflow-hidden relative shadow-lg shadow-primary/5">
                        <div className="w-48 h-48 bg-white p-4 rounded-3xl shadow-xl flex items-center justify-center shrink-0 border border-primary/10 -rotate-2">
                            <QrCode className="w-32 h-32 text-foreground opacity-20" />
                            <div className="absolute inset-0 flex items-center justify-center font-black text-xs text-primary/40 uppercase rotate-45 select-none">Scan Official</div>
                        </div>
                        <div className="space-y-4 flex-1">
                            <h3 className="text-2xl font-black text-foreground italic flex items-center gap-2">
                                QRIS Nasional <CheckCircle2 className="w-6 h-6 text-success" />
                            </h3>
                            <p className="text-sm font-medium text-muted-foreground leading-relaxed">
                                Gunakan aplikasi m-banking atau e-wallet (Gopay/OVO/ShopeePay) Anda untuk membayar melalui kode QR resmi sekolah. Sangat disarankan untuk nominal di bawah Rp 2.000.000 untuk kecepatan verifikasi.
                            </p>
                            <div className="flex flex-wrap gap-4 pt-4 opacity-50">
                                <span className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 bg-background border rounded-lg">Gopay</span>
                                <span className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 bg-background border rounded-lg">OVO</span>
                                <span className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 bg-background border rounded-lg">LinkAja</span>
                                <span className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 bg-background border rounded-lg">ShopeePay</span>
                            </div>
                        </div>
                        <div className="absolute top-[-20%] right-[-5%] w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
                    </div>

                    {/* Payment Method Details (Accordions) */}
                    <div className="space-y-4">
                        <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] ml-2 mb-6">Instruksi Pembayaran Detail</p>
                        {paymentMethods.map((method) => (
                            <div key={method.id} className="bg-surface border border-border-main rounded-[30px] overflow-hidden transition-all shadow-xs">
                                <button
                                    onClick={() => setExpandedMethod(expandedMethod === method.id ? null : method.id)}
                                    className="w-full p-8 flex items-center justify-between text-left hover:bg-primary-soft/30 transition-colors"
                                >
                                    <div className="flex items-center gap-6">
                                        <div className="w-12 h-12 bg-background border border-border-main rounded-2xl flex items-center justify-center text-primary shadow-xs">
                                            {method.name.includes('VA') ? <CreditCard className="w-6 h-6" /> : method.name.includes('QRIS') ? <Smartphone className="w-6 h-6" /> : <Building2 className="w-6 h-6" />}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-3">
                                                <p className="text-lg font-black text-foreground">{method.name}</p>
                                                <span className={`text-[9px] font-black px-2 py-0.5 rounded-md uppercase border ${method.type === 'Automated' ? 'bg-success/5 text-success border-success/20' : 'bg-warning/5 text-warning border-warning/20'}`}>
                                                    {method.type === 'Automated' ? 'Otomatis' : 'Manual'}
                                                </span>
                                            </div>
                                            <p className="text-xs font-bold text-muted-foreground mt-0.5">{method.description}</p>
                                        </div>
                                    </div>
                                    {expandedMethod === method.id ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                                </button>

                                {expandedMethod === method.id && (
                                    <div className="px-8 pb-10 pt-2 animate-in slide-in-from-top-4 duration-300">
                                        <div className="p-8 bg-background border border-border-main rounded-[24px] space-y-6">
                                            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                                                <HelpCircle className="w-4 h-4 text-primary" /> Tata Cara Pembayaran
                                            </p>
                                            <div className="space-y-4">
                                                {method.instructions.map((step, idx) => (
                                                    <div key={idx} className="flex gap-4 items-center">
                                                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center font-black text-[10px] text-primary shrink-0">{idx + 1}</div>
                                                        <p className="text-xs font-medium text-muted-foreground leading-relaxed">{step}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right: Security & Policy */}
                <div className="lg:col-span-12 xl:col-span-4 space-y-8">
                    <div className="bg-surface border border-border-main rounded-[40px] p-10 shadow-xs space-y-8 sticky top-28">
                        <div>
                            <h3 className="text-xl font-black flex items-center gap-3 italic">
                                Kebijakan Keamanan
                            </h3>
                            <p className="text-xs text-muted-foreground font-medium mt-2 leading-relaxed">
                                Sistem pembayaran kami dirancang untuk melindungi setiap rupiah yang Anda bayarkan untuk pendidikan buah hati.
                            </p>
                        </div>

                        <div className="space-y-6">
                            <div className="flex gap-4">
                                <div className="w-10 h-10 rounded-2xl bg-success/10 flex items-center justify-center text-success shrink-0">
                                    <ShieldCheck className="w-5 h-5" />
                                </div>
                                <div className="space-y-1">
                                    <p className="text-xs font-black text-foreground">Akses Terkelola Terpusat</p>
                                    <p className="text-[10px] text-muted-foreground font-medium italic">Metode pembayaran dikelola langsung oleh unit keuangan sekolah dan yayasan. Orang tua tidak dapat mengubah detail rekening untuk keamanan bersama.</p>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                                    <Info className="w-5 h-5" />
                                </div>
                                <div className="space-y-1">
                                    <p className="text-xs font-black text-foreground">Anti-Fraud Protection</p>
                                    <p className="text-[10px] text-muted-foreground font-medium italic">Sistem membatasi input nomor rekening secara manual agar Anda tidak salah transfer ke rekening yang tidak terdaftar.</p>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <div className="w-10 h-10 rounded-2xl bg-warning/10 flex items-center justify-center text-warning shrink-0">
                                    <ArrowRight className="w-5 h-5" />
                                </div>
                                <div className="space-y-1">
                                    <p className="text-xs font-black text-foreground">Sinkronisasi Keuangan</p>
                                    <p className="text-[10px] text-muted-foreground font-medium italic">Seluruh riwayat pembayaran Anda tercatat dalam ledger permanen yang dapat diaudit oleh yayasan sewaktu-waktu.</p>
                                </div>
                            </div>
                        </div>

                        <div className="pt-8 border-t border-border-main text-center">
                            <p className="text-[9px] font-bold text-muted-foreground italic uppercase">Butuh bantuan?</p>
                            <button className="text-primary font-black text-[10px] uppercase mt-2 hover:underline">Hubungi Admin Keuangan Sekolah</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
